import { t } from '@vitesse/base-ui/i18n';
import type { FindingKindWord, FindingStatusWord } from '@vitesse/base-ui/ai';

/*
 * WHAT A FINDING'S IDS ARE CALLED, for a person reading the log.
 *
 * An entry stores ids: "<relay>/<controller>" for what its evidence read, a model id, the user id of
 * whoever recorded or decided it. A person reads names — "Core · Line A", "Miyu" — and the names are
 * already in the app: the relays' managers hold their controllers, and the plane stored
 * each person's name beside their id. An id nothing here can name is shown as it is rather than
 * hidden: an entry about a controller since removed is still about it.
 *
 * Pure, so the rules are tested without a page.
 */

/** The least of a relay this needs: what the shell's gateway entries already carry. */
export interface RelayLike {
  gateway: { deviceFingerprint: string; displayName?: string; hostname?: string };
  controllers: { all: readonly { id: string; name: string }[] };
}

export interface FindingNames {
  /** The org's relays, for the relay filter. */
  relays: { id: string; label: string }[];
  /** Every relay's controllers, as "<relay>/<controller>", for the controller filter. */
  controllers: { id: string; label: string; relay: string }[];
  /** "gw-1/core" → "Core · Line A"; a controller the model named without a relay → its name, or the id. */
  controller(id: string): string;
  /** A user id and the name stored beside it → the name; "you" for yourself; else the id. */
  person(userId: string, name: string): string;
}

export function findingNames(relays: readonly RelayLike[], selfId: string): FindingNames {
  const relayLabel = new Map(
    relays.map((r) => [r.gateway.deviceFingerprint, r.gateway.displayName || r.gateway.hostname || r.gateway.deviceFingerprint]),
  );
  const controllerOn = (relay: string, id: string) =>
    relays.find((r) => r.gateway.deviceFingerprint === relay)?.controllers.all.find((c) => c.id === id)?.name;
  return {
    relays: [...relayLabel].map(([id, label]) => ({ id, label })),
    controllers: relays.flatMap((r) =>
      r.controllers.all.map((c) => ({
        id: `${r.gateway.deviceFingerprint}/${c.id}`,
        label: `${c.name || c.id} · ${relayLabel.get(r.gateway.deviceFingerprint)}`,
        relay: r.gateway.deviceFingerprint,
      })),
    ),
    controller(id) {
      const cut = id.indexOf('/');
      if (cut < 0) {
        const named = relays.flatMap((r) => r.controllers.all).find((c) => c.id === id);
        return named?.name || id;
      }
      const relay = id.slice(0, cut);
      const controller = id.slice(cut + 1);
      return `${controllerOn(relay, controller) || controller} · ${relayLabel.get(relay) ?? relay}`;
    },
    person(userId, name) {
      if (name) return name;
      return userId && userId === selfId ? t('findings.you') : userId;
    },
  };
}

/** What an entry is about, in a cell: the first controller, and how many more. */
export function aboutLabel(controllerIds: readonly string[], names: Pick<FindingNames, 'controller'>): string {
  if (!controllerIds.length) return '';
  const first = names.controller(controllerIds[0]!);
  return controllerIds.length === 1 ? first : t('findings.aboutMore', { first, n: controllerIds.length - 1 });
}

/** The decisions a person can make on an entry in this status — none once it is closed, or when it
 *  has nothing to decide. The same moves the plane allows (findings.ts `mayMove`). */
export type Decision = 'accepted' | 'done' | 'declined';
export function decisionsFor(status: FindingStatusWord): Decision[] {
  if (status === 'proposed') return ['accepted', 'declined'];
  if (status === 'accepted') return ['done', 'declined'];
  return [];
}

export function kindLabel(kind: FindingKindWord): string {
  switch (kind) {
    case 'baseline':
      return t('findings.kindBaseline');
    case 'change':
      return t('findings.kindChange');
    case 'anomaly':
      return t('findings.kindAnomaly');
    case 'proposal':
      return t('findings.kindProposal');
    case 'definition':
      return t('findings.kindDefinition');
    default:
      return kind;
  }
}

export function statusLabel(status: FindingStatusWord): string {
  switch (status) {
    case 'proposed':
      return t('findings.statusProposed');
    case 'accepted':
      return t('findings.statusAccepted');
    case 'done':
      return t('findings.statusDone');
    case 'declined':
      return t('findings.statusDeclined');
    default:
      return '';
  }
}

/** How the claim was reached, and how sure — words the recorder chose from a fixed set. */
export function basisLabel(provenance: string, confidence: string): string {
  const how =
    provenance === 'measured'
      ? t('findings.measured')
      : provenance === 'derived'
        ? t('findings.derived')
        : provenance === 'modelled'
          ? t('findings.modelled')
          : provenance;
  const sure =
    confidence === 'low'
      ? t('findings.confidenceLow')
      : confidence === 'medium'
        ? t('findings.confidenceMedium')
        : confidence === 'high'
          ? t('findings.confidenceHigh')
          : confidence;
  return [how, sure].filter(Boolean).join(' · ');
}

/** The colour a status reads in: waiting is yellow, under way blue, done green, declined grey. */
export function statusTone(status: FindingStatusWord): 'yellow' | 'blue' | 'green' | 'gray' {
  return status === 'proposed' ? 'yellow' : status === 'accepted' ? 'blue' : status === 'done' ? 'green' : 'gray';
}

/** The button that makes a decision, and the dialog's heading and line for it. */
export function decisionText(d: Decision, id: string): { button: string; heading: string; hint: string } {
  switch (d) {
    case 'accepted':
      return { button: t('findings.accept'), heading: t('findings.acceptHeading', { id }), hint: t('findings.acceptHint') };
    case 'done':
      return { button: t('findings.complete'), heading: t('findings.completeHeading', { id }), hint: t('findings.completeHint') };
    case 'declined':
      return { button: t('findings.decline'), heading: t('findings.declineHeading', { id }), hint: t('findings.declineHint') };
  }
}
